/**
 * Stripe customer portal ("Manage subscription") and extra-call checkout,
 * opened through the payment backend so the user doesn't have to log in to
 * Stripe again.
 */
import { auth } from './firebase';
import { MarketId } from './market';

export const PAYMENT_BACKEND_URL = 'https://payment-backend-f3r77qlg3q-uc.a.run.app';

/**
 * Returns a one-time Stripe portal URL for the signed-in user. With
 * 'change_plan' the portal opens on the plan picker for their subscription.
 */
export async function createBillingPortalUrl(flow?: 'change_plan'): Promise<string> {
  const token = await auth.currentUser?.getIdToken();
  const res = await fetch(`${PAYMENT_BACKEND_URL}/billing-portal`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(flow ? { flow } : {}),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok || !body.url) {
    if (res.status === 404) throw new Error('No billing account found for this login yet.');
    if (res.status === 401) throw new Error('Please sign in again.');
    throw new Error('Could not open billing right now. Please try again.');
  }
  return body.url;
}

/**
 * Returns a Stripe Checkout URL for extra calls. Subscribers get the lower
 * per-call price; the buyer picks how many in Checkout.
 */
export async function createExtraCallsCheckoutUrl(market: MarketId): Promise<string> {
  const token = await auth.currentUser?.getIdToken();
  const res = await fetch(`${PAYMENT_BACKEND_URL}/extra-calls-checkout`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ market }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok || !body.url) {
    if (res.status === 401) throw new Error('Please sign in again.');
    if (res.status === 503) throw new Error("Extra calls aren't available to buy yet.");
    throw new Error('Could not open checkout right now. Please try again.');
  }
  return body.url;
}
